/**
 * MOVRA shared storefront behaviour.
 * Small, dependency-free custom elements. No product data lives here: every value
 * (variants, prices, media, copy) is rendered by Liquid from Shopify and read from the DOM.
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

/* ------------------------------------------------------------------ */
/* Utilities                                                           */
/* ------------------------------------------------------------------ */

const FOCUSABLE =
  'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, video[controls], [tabindex]:not([tabindex="-1"])';

export function t(key, fallback = '') {
  return window.MovraConfig?.strings?.[key] ?? fallback;
}

/** Screen-reader announcement through a single polite live region. */
export function announce(message) {
  let region = document.getElementById('m-live-region');
  if (!region) {
    region = document.createElement('div');
    region.id = 'm-live-region';
    region.className = 'm-visually-hidden';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    document.body.appendChild(region);
  }
  region.textContent = '';
  window.setTimeout(() => {
    region.textContent = message;
  }, 60);
}

let lockCount = 0;
export function lockScroll() {
  lockCount += 1;
  document.documentElement.classList.add('m-scroll-locked');
}
export function unlockScroll() {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount === 0) document.documentElement.classList.remove('m-scroll-locked');
}

/**
 * Opens a native <dialog> as a modal. Native modal dialogs make the rest of the page inert
 * (focus is contained) and fire `cancel` on Escape. We add scroll locking and focus restoration.
 */
export function openDialog(dialog, { initialFocus, onClose } = {}) {
  if (!dialog || dialog.open) return;
  const returnFocus = document.activeElement;
  dialog.showModal();
  lockScroll();
  document.documentElement.classList.add('m-modal-open');
  const target =
    (initialFocus && dialog.querySelector(initialFocus)) ||
    dialog.querySelector('[autofocus]') ||
    dialog.querySelector(FOCUSABLE);
  target?.focus({ preventScroll: true });

  const handleClose = () => {
    unlockScroll();
    if (!document.querySelector('dialog[open].m-dialog')) {
      document.documentElement.classList.remove('m-modal-open');
    }
    dialog.removeEventListener('close', handleClose);
    dialog.removeEventListener('click', handleBackdrop);
    onClose?.();
    const active = document.activeElement;
    const focusLost = !active || active === document.body || dialog.contains(active);
    if (focusLost && returnFocus instanceof HTMLElement && returnFocus.isConnected) {
      returnFocus.focus({ preventScroll: true });
    }
  };
  const handleBackdrop = (event) => {
    // Click on the ::backdrop reports the dialog itself as target.
    if (event.target === dialog) dialog.close();
  };
  dialog.addEventListener('close', handleClose);
  dialog.addEventListener('click', handleBackdrop);
}

/* ------------------------------------------------------------------ */
/* Cart – native Shopify AJAX Cart API + Horizon's cart drawer events  */
/* ------------------------------------------------------------------ */

let eventsModule = null;
async function getEvents() {
  if (eventsModule !== null) return eventsModule;
  try {
    eventsModule = await import('@shopify/events');
  } catch (error) {
    eventsModule = false;
  }
  return eventsModule;
}

function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
}

/**
 * Adds one or more real Shopify variants to the cart.
 * @param {Array<{id: number|string, quantity: number, selling_plan?: string, properties?: object}>} items
 * @param {{ source?: Element }} [options]
 * @returns {Promise<{cart: object}>}
 */
export async function addToCart(items, { source = document } = {}) {
  const routes = window.Theme?.routes || {};
  const addUrl = routes.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`;
  const cartUrl = `${routes.cart_url || `${window.Shopify?.routes?.root || '/'}cart`}.js`;
  const sections = cartSectionIds();
  const events = await getEvents();

  const cleanItems = items
    .filter((item) => item && item.id)
    .map((item) => {
      const line = { id: Number(item.id), quantity: Math.max(1, Number(item.quantity) || 1) };
      if (item.selling_plan) line.selling_plan = item.selling_plan;
      if (item.properties) line.properties = item.properties;
      return line;
    });

  let deferred = null;
  if (events && events.CartLinesUpdateEvent) {
    deferred = events.CartLinesUpdateEvent.createPromise();
    source.dispatchEvent(
      new events.CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: cleanItems.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
        promise: deferred.promise,
      })
    );
  }

  const itemCount = cleanItems.reduce((sum, line) => sum + line.quantity, 0);

  try {
    const response = await fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: cleanItems, sections: sections.join(',') }),
    });
    const data = await response.json();
    const cart = await fetch(cartUrl, { headers: { Accept: 'application/json' } }).then((r) => r.json());

    if (!response.ok || data.status) {
      const message = data.description || data.message || t('cart_error', 'Something went wrong. Please try again.');
      if (events && events.CartErrorEvent) {
        source.dispatchEvent(
          new events.CartErrorEvent({ error: message, code: 'INVALID', detail: { description: data.description } })
        );
      }
      deferred?.resolve({
        cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, itemCount, source: 'movra' },
      });
      const error = new Error(message);
      error.cart = cart;
      throw error;
    }

    deferred?.resolve({
      cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { didError: false, items: cart.items, itemCount, source: 'movra', sections: data.sections },
    });

    if (!deferred) {
      // Standard events unavailable: keep header count accurate without a full reload.
      document.querySelectorAll('[data-m-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
      });
    }

    announce(t('added_to_cart', 'Added to cart'));
    return { cart };
  } catch (error) {
    if (!error.cart) deferred?.reject(error);
    announce(error.message || t('cart_error', 'Something went wrong. Please try again.'));
    throw error;
  }
}

/* ------------------------------------------------------------------ */
/* <m-header> – sticky state, desktop dropdowns, mobile drawer         */
/* ------------------------------------------------------------------ */

class MHeader extends HTMLElement {
  connectedCallback() {
    this.sentinel = document.createElement('div');
    this.sentinel.setAttribute('aria-hidden', 'true');
    this.sentinel.style.cssText = 'height:1px;margin-bottom:-1px;flex:none;pointer-events:none;';
    (document.querySelector('.page-wrapper') || document.body).prepend(this.sentinel);
    this.io = new IntersectionObserver(([entry]) => {
      this.toggleAttribute('data-scrolled', !entry.isIntersecting);
    });
    this.io.observe(this.sentinel);

    this.ro = new ResizeObserver(() => {
      const h = this.offsetHeight;
      document.body.style.setProperty('--header-height', `${h}px`);
      document.body.style.setProperty('--header-group-height', `${h}px`);
      document.documentElement.style.setProperty('--m-header-offset', `${h}px`);
    });
    this.ro.observe(this);

    this.dropdowns = [...this.querySelectorAll('[data-m-dropdown]')];
    this.dropdowns.forEach((item) => this.bindDropdown(item));

    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onDocumentClick);

    this.drawer = this.querySelector('[data-m-drawer]');
    this.querySelector('[data-m-drawer-open]')?.addEventListener('click', (event) => {
      const trigger = event.currentTarget;
      openDialog(this.drawer, {
        onClose: () => trigger.setAttribute('aria-expanded', 'false'),
      });
      trigger.setAttribute('aria-expanded', 'true');
    });
    this.querySelectorAll('[data-m-drawer-close]').forEach((button) =>
      button.addEventListener('click', () => this.drawer?.close())
    );
    // Close the drawer when a same-page anchor is followed.
    this.drawer?.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (link) this.drawer.close();
    });

    this.desktopQuery = window.matchMedia('(min-width: 1100px)');
    this.desktopQuery.addEventListener('change', this.onBreakpoint);
  }

  disconnectedCallback() {
    this.io?.disconnect();
    this.ro?.disconnect();
    this.sentinel?.remove();
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocumentClick);
    this.desktopQuery?.removeEventListener('change', this.onBreakpoint);
  }

  onBreakpoint = (event) => {
    if (event.matches && this.drawer?.open) this.drawer.close();
    this.closeAll();
  };

  bindDropdown(item) {
    const button = item.querySelector('[data-m-dropdown-toggle]');
    let hoverTimer;
    button?.addEventListener('click', () => {
      const open = button.getAttribute('aria-expanded') === 'true';
      // A mouse click right after hover-open should keep the panel open rather than toggle it shut.
      if (open && Date.now() - (item.hoverOpenedAt || 0) < 500) return;
      this.closeAll(item);
      this.setOpen(item, !open);
    });
    item.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse') return;
      clearTimeout(hoverTimer);
      this.closeAll(item);
      if (!item.hasAttribute('data-open')) item.hoverOpenedAt = Date.now();
      this.setOpen(item, true);
    });
    item.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'mouse') return;
      hoverTimer = setTimeout(() => this.setOpen(item, false), 120);
    });
    item.addEventListener('focusout', (event) => {
      if (!item.contains(event.relatedTarget)) this.setOpen(item, false);
    });
  }

  setOpen(item, open) {
    const button = item.querySelector('[data-m-dropdown-toggle]');
    button?.setAttribute('aria-expanded', String(open));
    item.toggleAttribute('data-open', open);
  }

  closeAll(except) {
    this.dropdowns?.forEach((item) => {
      if (item !== except) this.setOpen(item, false);
    });
  }

  onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const openItem = this.dropdowns?.find((item) => item.hasAttribute('data-open'));
    if (openItem) {
      this.setOpen(openItem, false);
      openItem.querySelector('[data-m-dropdown-toggle]')?.focus();
    }
  };

  onDocumentClick = (event) => {
    if (!this.contains(event.target)) this.closeAll();
  };
}

/* ------------------------------------------------------------------ */
/* <m-carousel> – drag/swipe rail with buttons and progress            */
/* ------------------------------------------------------------------ */

class MCarousel extends HTMLElement {
  connectedCallback() {
    this.rail = this.querySelector('[data-m-rail]');
    if (!this.rail) return;
    this.prev = this.querySelectorAll('[data-m-prev]');
    this.next = this.querySelectorAll('[data-m-next]');
    this.progress = this.querySelector('[data-m-progress]');
    this.bar = this.progress?.querySelector('.m-progress__bar');

    this.prev.forEach((b) => b.addEventListener('click', () => this.step(-1)));
    this.next.forEach((b) => b.addEventListener('click', () => this.step(1)));
    this.rail.addEventListener('scroll', this.onScroll, { passive: true });
    this.ro = new ResizeObserver(this.update);
    this.ro.observe(this.rail);

    this.rail.addEventListener('pointerdown', this.onPointerDown);
    this.rail.addEventListener('click', this.onClickCapture, true);
    this.update();
  }

  disconnectedCallback() {
    this.ro?.disconnect();
  }

  get pageSize() {
    const item = this.rail.firstElementChild;
    if (!item) return this.rail.clientWidth;
    const gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
    const itemWidth = item.getBoundingClientRect().width + gap;
    const perView = Math.max(1, Math.floor((this.rail.clientWidth + gap) / itemWidth));
    return itemWidth * perView;
  }

  step(direction) {
    this.rail.scrollBy({ left: direction * this.pageSize, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  }

  onScroll = () => {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  };

  update = () => {
    const { scrollLeft, scrollWidth, clientWidth } = this.rail;
    const max = Math.max(0, scrollWidth - clientWidth);
    const atStart = scrollLeft <= 2;
    const atEnd = scrollLeft >= max - 2;
    this.prev.forEach((b) => (b.disabled = atStart));
    this.next.forEach((b) => (b.disabled = atEnd));
    if (this.progress && this.bar) {
      const visible = scrollWidth ? clientWidth / scrollWidth : 1;
      this.progress.hidden = visible >= 0.995;
      const width = Math.min(1, visible);
      const left = max ? (scrollLeft / max) * (1 - width) : 0;
      this.bar.style.width = `${width * 100}%`;
      this.bar.style.transform = `translateX(${(left / width) * 100}%)`;
    }
  };

  onPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    const startX = event.clientX;
    const startScroll = this.rail.scrollLeft;
    this.dragged = false;
    const move = (e) => {
      const dx = e.clientX - startX;
      if (!this.dragged && Math.abs(dx) > 6) {
        this.dragged = true;
        this.rail.classList.add('is-dragging');
      }
      if (this.dragged) this.rail.scrollLeft = startScroll - dx;
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      if (this.dragged) {
        this.rail.classList.remove('is-dragging');
        // Let snapping settle to the nearest item.
        const item = this.rail.firstElementChild;
        if (item) {
          const gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
          const w = item.getBoundingClientRect().width + gap;
          this.rail.scrollTo({ left: Math.round(this.rail.scrollLeft / w) * w, behavior: 'smooth' });
        }
      }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  onClickCapture = (event) => {
    if (this.dragged) {
      event.preventDefault();
      event.stopPropagation();
      this.dragged = false;
    }
  };
}

/* ------------------------------------------------------------------ */
/* <m-tabs> – WAI-ARIA tabs with roving tabindex                       */
/* ------------------------------------------------------------------ */

class MTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = this.tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls')));
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index, true));
      tab.addEventListener('keydown', (event) => this.onKey(event, index));
    });
    const initial = Math.max(
      0,
      this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
    );
    this.select(initial, false);
  }

  onKey(event, index) {
    const vertical = getComputedStyle(this.querySelector('[role="tablist"]')).flexDirection === 'column';
    const prevKeys = vertical ? ['ArrowUp', 'ArrowLeft'] : ['ArrowLeft'];
    const nextKeys = vertical ? ['ArrowDown', 'ArrowRight'] : ['ArrowRight'];
    let target = null;
    if (prevKeys.includes(event.key)) target = (index - 1 + this.tabs.length) % this.tabs.length;
    if (nextKeys.includes(event.key)) target = (index + 1) % this.tabs.length;
    if (event.key === 'Home') target = 0;
    if (event.key === 'End') target = this.tabs.length - 1;
    if (target === null) return;
    event.preventDefault();
    this.select(target, true);
  }

  select(index, focus) {
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      const panel = this.panels[i];
      if (!panel) return;
      if (active) {
        panel.removeAttribute('data-collapsed');
        panel.classList.remove('is-entering');
        void panel.offsetWidth;
        panel.classList.add('is-entering');
      } else {
        panel.setAttribute('data-collapsed', '');
      }
    });
    if (focus) {
      this.tabs[index].focus();
      this.tabs[index].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }
  }
}

/* ------------------------------------------------------------------ */
/* <m-accordion> – button + region disclosure with height animation    */
/* ------------------------------------------------------------------ */

class MAccordion extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('[data-m-acc-toggle]').forEach((button) => {
      const panel = document.getElementById(button.getAttribute('aria-controls'));
      if (!panel) return;
      if (button.getAttribute('aria-expanded') !== 'true') panel.setAttribute('data-collapsed', '');
      button.addEventListener('click', () => this.toggle(button, panel));
    });
  }

  toggle(button, panel) {
    const open = button.getAttribute('aria-expanded') === 'true';
    if (this.hasAttribute('single') && !open) {
      this.querySelectorAll('[data-m-acc-toggle][aria-expanded="true"]').forEach((other) => {
        if (other !== button) this.toggle(other, document.getElementById(other.getAttribute('aria-controls')));
      });
    }
    button.setAttribute('aria-expanded', String(!open));
    if (reducedMotion.matches) {
      panel.toggleAttribute('data-collapsed', open);
      return;
    }
    if (open) {
      panel.style.height = `${panel.scrollHeight}px`;
      requestAnimationFrame(() => {
        panel.style.height = '0px';
      });
      panel.addEventListener(
        'transitionend',
        () => {
          panel.setAttribute('data-collapsed', '');
          panel.style.height = '';
        },
        { once: true }
      );
    } else {
      panel.removeAttribute('data-collapsed');
      panel.style.height = '0px';
      requestAnimationFrame(() => {
        panel.style.height = `${panel.scrollHeight}px`;
      });
      panel.addEventListener(
        'transitionend',
        () => {
          panel.style.height = '';
        },
        { once: true }
      );
    }
  }
}

/* ------------------------------------------------------------------ */
/* <m-hero-video> – single responsive source, muted autoplay, pause    */
/* ------------------------------------------------------------------ */

class MHeroVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.button = this.querySelector('[data-m-video-toggle]');
    if (!this.video) return;
    this.mobileQuery = window.matchMedia('(max-width: 767px)');
    this.userPaused = false;
    this.applySource();
    this.mobileQuery.addEventListener('change', this.applySource);
    this.button?.addEventListener('click', this.toggle);
    this.io = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) this.video.pause();
      else if (this.shouldAutoplay && !this.userPaused) this.play();
      this.sync();
    });
    this.io.observe(this);
    reducedMotion.addEventListener('change', this.onMotionChange);
  }

  disconnectedCallback() {
    this.io?.disconnect();
    this.mobileQuery?.removeEventListener('change', this.applySource);
    reducedMotion.removeEventListener('change', this.onMotionChange);
  }

  get shouldAutoplay() {
    return this.hasAttribute('autoplay') && !reducedMotion.matches;
  }

  onMotionChange = () => {
    if (reducedMotion.matches) this.video.pause();
    this.sync();
  };

  applySource = () => {
    const mobile = this.mobileQuery.matches;
    const src = (mobile && this.dataset.srcMobile) || this.dataset.srcDesktop || this.dataset.srcMobile;
    const poster = (mobile && this.dataset.posterMobile) || this.dataset.posterDesktop || '';
    if (!src || this.video.dataset.current === src) return;
    this.video.dataset.current = src;
    if (poster) this.video.poster = poster;
    this.video.src = src;
    this.video.muted = true;
    this.video.load();
    if (this.shouldAutoplay && !this.userPaused) this.play();
  };

  play() {
    this.video.muted = true;
    this.video.play().catch(() => {}).finally(() => this.sync());
  }

  toggle = () => {
    if (this.video.paused) {
      this.userPaused = false;
      this.play();
    } else {
      this.userPaused = true;
      this.video.pause();
    }
    this.sync();
  };

  sync() {
    if (!this.button) return;
    const playing = !this.video.paused;
    this.button.setAttribute('aria-pressed', String(!playing));
    this.button.setAttribute('aria-label', playing ? this.button.dataset.labelPause : this.button.dataset.labelPlay);
    this.button.toggleAttribute('data-playing', playing);
  }
}

/* ------------------------------------------------------------------ */
/* <m-video-modal> – one accessible modal for a rail of videos         */
/* ------------------------------------------------------------------ */

class MVideoModal extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.stage = this.dialog?.querySelector('[data-m-video-stage]');
    this.title = this.dialog?.querySelector('[data-m-video-title]');
    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-m-video-open]');
      if (!trigger || !this.contains(trigger)) return;
      this.open(trigger);
    });
    this.dialog?.querySelector('[data-m-video-close]')?.addEventListener('click', () => this.dialog.close());
  }

  open(trigger) {
    const template = document.getElementById(trigger.dataset.mVideoOpen);
    if (!template || !this.stage) return;
    this.stage.replaceChildren(template.content.cloneNode(true));
    if (this.title) this.title.textContent = trigger.dataset.title || '';
    openDialog(this.dialog, {
      initialFocus: '[data-m-video-close]',
      onClose: () => this.stage.replaceChildren(),
    });
    const video = this.stage.querySelector('video');
    if (video) video.play().catch(() => {});
  }
}

/* ------------------------------------------------------------------ */
/* Quick add – direct add for single-variant products, option picker   */
/* dialog (Section Rendering API) when a choice is required.           */
/* ------------------------------------------------------------------ */

let quickDialog = null;
function getQuickDialog() {
  if (quickDialog) return quickDialog;
  quickDialog = document.createElement('dialog');
  quickDialog.className = 'm-dialog m-quick-dialog';
  quickDialog.setAttribute('aria-label', t('choose_options', 'Choose options'));
  quickDialog.innerHTML = `<div class="m-quick-dialog__panel"><button type="button" class="m-float-btn m-quick-dialog__close" aria-label="${t(
    'close',
    'Close'
  )}"><svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg></button><div class="m-quick-dialog__body" data-m-quick-body></div></div>`;
  quickDialog.querySelector('.m-quick-dialog__close').addEventListener('click', () => quickDialog.close());
  document.body.appendChild(quickDialog);
  return quickDialog;
}

async function openQuickAdd(button) {
  const url = button.dataset.productUrl;
  if (!url) return;
  button.setAttribute('aria-busy', 'true');
  try {
    const separator = url.includes('?') ? '&' : '?';
    const response = await fetch(`${url}${separator}section_id=movra-quick-add`);
    if (!response.ok) throw new Error(response.statusText);
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const content = doc.querySelector('[data-m-quick-content]');
    if (!content) throw new Error('Missing quick add content');
    const { product: productJs, productCss } = window.MovraConfig?.assets || {};
    if (productCss && !document.querySelector(`link[href="${productCss}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = productCss;
      document.head.appendChild(link);
      await new Promise((resolve) => {
        link.onload = resolve;
        link.onerror = resolve;
      });
    }
    if (productJs) await import(productJs);
    const dialog = getQuickDialog();
    dialog.querySelector('[data-m-quick-body]').replaceChildren(content);
    openDialog(dialog, { onClose: () => dialog.querySelector('[data-m-quick-body]').replaceChildren() });
  } catch (error) {
    // Fall back to the product page so the shopper can still choose options.
    window.location.href = url;
  } finally {
    button.removeAttribute('aria-busy');
  }
}

document.addEventListener('movra:added', () => {
  if (quickDialog?.open) quickDialog.close();
});

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-m-quick-add]');
  if (!button || button.disabled) return;
  event.preventDefault();

  if (button.dataset.variantId) {
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    const label = button.querySelector('[data-m-label]');
    const original = label?.textContent;
    try {
      await addToCart([{ id: button.dataset.variantId, quantity: 1 }], { source: button });
      if (label) label.textContent = t('added', 'Added');
      setTimeout(() => {
        if (label && original) label.textContent = original;
      }, 2000);
    } catch (error) {
      if (label) label.textContent = t('cart_error_short', 'Try again');
      setTimeout(() => {
        if (label && original) label.textContent = original;
      }, 2500);
    } finally {
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
    return;
  }

  openQuickAdd(button);
});

/* ------------------------------------------------------------------ */
/* Register                                                            */
/* ------------------------------------------------------------------ */

const define = (name, ctor) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
};
define('m-header', MHeader);
define('m-carousel', MCarousel);
define('m-tabs', MTabs);
define('m-accordion', MAccordion);
define('m-hero-video', MHeroVideo);
define('m-video-modal', MVideoModal);

window.Movra = Object.assign(window.Movra || {}, { addToCart, announce, openDialog, lockScroll, unlockScroll });
